import { useEffect, useRef } from 'react';
import {
  QueryFunction,
  QueryKey,
  QueryStatus,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';

interface IIUseFetchReturns<T> {
  isLoading: boolean;
  data: T | undefined;
  refetch: () => Promise<any>;
  status: QueryStatus;
}

interface FetchOptions<T>
  extends Omit<
    UseQueryOptions<T, unknown, T, QueryKey>,
    'queryKey' | 'queryFn'
  > {
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
}

function useFetch<T>(
  queryId: QueryKey,
  queryFn: QueryFunction<T>,
  options: FetchOptions<T> = {},
): IIUseFetchReturns<T> {
  const { isLoading, data, refetch, isSuccess, isError, error, status } =
    useQuery<T, unknown>({
      queryKey: queryId,
      queryFn,
      ...options,
    });

  // Prevents onSuccess from firing twice when wrappedRefetch is used:
  // wrappedRefetch calls onSuccess in .then(), then this useEffect would
  // fire it again on the same data. The ref skips the duplicate.
  const handledByRefetch = useRef(false);

  useEffect(() => {
    if (isSuccess && options.onSuccess && !handledByRefetch.current) {
      options.onSuccess(data);
    }
    handledByRefetch.current = false;
  }, [isSuccess, data]);

  const wrappedRefetch = () => {
    handledByRefetch.current = true;
    return refetch().then((res) => {
      if (res.data && options.onSuccess) {
        options.onSuccess(res.data);
      }
      return res;
    });
  };

  useEffect(() => {
    if (isError && options.onError) {
      options.onError(error);
    }
  }, [isError, error]);

  return { data, isLoading, status, refetch: wrappedRefetch };
}

export default useFetch;
